/* eslint-disable no-useless-computed-key */
import React, { useEffect, useState } from "react"
import styles from "./UserInfo.module.scss"
import UserImage from "../UserImage/UserImage"

import Button from "../Button/Button"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import { genderOptions, raceOptions } from "./helpers"
import { nameValidaton } from "./validation"
import { objectDeepCopy } from "../../helpers"
import { options as professionOptionsRaw } from "../filters/MemberSearchFilter/wrappers/ProfessionDropdown/options"
import { options as experienceOptionsRaw } from "../filters/MemberSearchFilter/wrappers/ExperienceDropdown/options"
import { options as industryOptionsRaw } from "../filters/MemberSearchFilter/wrappers/IndustryDropdown/options"

import UserInfoShowInfo from "./UserInfoShowInfo"
import ConfirmCancelPopup from "../Popup/ConfirmCancelPopup"

import ReportButton from "../ReportButton/ReportButton"
import { Prompt } from "react-router-dom"
import { messages } from "../../helpers/constants"

const UserInfo = ({
  image,
  openPhotoPopup,
  anotherUser,
  onOpenChat,
  fullName,
  onChangeNameAndRole,
  onDeletePhotoProfile,
  profession,
  experience,
  industry,
  reported,
  onReport,
  gender,
  isBlocked,
  race,
  userRole,
  onBlock,
  userType,
}) => {
  const normalizeOptions = (items) =>
    items
      .filter((item) => item.value !== "All") // Filter out "All" for profile edit
      .map((item) => ({
        ...item,
        databaseName: item.databaseName ?? item.id,
      }))
  const professionOptions = normalizeOptions(professionOptionsRaw)
  const experienceOptions = normalizeOptions(experienceOptionsRaw)
  const industryOptions = normalizeOptions(industryOptionsRaw)
  const [isEditing, setIsEditing] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [isPopupOpened, setIsPopupOpened] = useState(false)
  const [name, setName] = useState({ showedName: "", editedName: "" })

  const [errors, setErrors] = useState({
    fullName: { valid: true, errorMessage: "" },
  })

  const [dropdownData, setDropdownData] = useState({
    profession: {
      showed: { value: "" },
      edited: { value: "" },
    },
    experience: {
      showed: { value: "" },
      edited: { value: "" },
    },
    industry: {
      showed: { value: "" },
      edited: { value: "" },
    },
    gender: {
      showed: { value: "" },
      edited: { value: "" },
    },
    race: {
      showed: { value: "" },
      edited: { value: "" },
    },
  })

  useEffect(() => {
    const prof =
      userType === "freelancer" && profession
        ? { value: profession, databaseName: profession }
        : (profession &&
            professionOptions.find(
              (option) => option.databaseName === profession,
            )) ||
          professionOptions[0]
    const exp =
      (experience &&
        experienceOptions.find(
          (option) => option.databaseName === experience,
        )) ||
      experienceOptions[0]
    const industr =
      (industry &&
        industryOptions.find((option) => option.databaseName === industry)) ||
      industryOptions[0]
    const gendr =
      (gender &&
        genderOptions.find((option) => option.databaseName === gender)) ||
      genderOptions[0]
    const rac =
      (race && raceOptions.find((option) => option.databaseName === race)) ||
      raceOptions[0]
    setDropdownData({
      profession: { showed: prof, edited: prof },
      experience: { showed: exp, edited: exp },
      industry: { showed: industr, edited: industr },
      gender: { showed: gendr, edited: gendr },
      race: { showed: rac, edited: rac },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profession, experience, industry, gender, race, userType])

  useEffect(() => {
    if (fullName) {
      setName({ showedName: fullName, editedName: fullName })
    }
  }, [fullName])

  const checkToSave = () => {
    return Object.values(errors).some((item) => item.valid === false)
  }

  const handleCancel = () => {
    const data = objectDeepCopy(dropdownData)
    setIsEditing(false)
    setHasChanges(false)
    setIsPopupOpened(false)
    setDropdownData({
      ...data,
      profession: { ...data.profession, edited: data.profession.showed },
      experience: { ...data.experience, edited: data.experience.showed },
      industry: { ...data.industry, edited: data.industry.showed },
      gender: { ...data.gender, edited: data.gender.showed },
      race: { ...data.race, edited: data.race.showed },
    })
    setName({ ...name, editedName: name.showedName })
  }

  const onCancelClick = () => {
    if (hasChanges) {
      setIsPopupOpened(true)
    } else {
      setIsEditing(false)
    }
  }

  const handleSave = () => {
    const data = objectDeepCopy(dropdownData)
    setIsEditing(false)
    setHasChanges(false)
    setDropdownData({
      ...data,
      profession: { ...data.profession, showed: data.profession.edited },
      experience: { ...data.experience, showed: data.experience.edited },
      industry: { ...data.industry, showed: data.industry.edited },
      race: { ...data.race, showed: data.race.edited },
      gender: { ...data.gender, showed: data.gender.edited },
    })
    setName({ ...name, showedName: name.editedName })
    onChangeNameAndRole({
      ["full_name"]: name.editedName,
      ["profession"]: dropdownData.profession.edited.databaseName || "",
      ["experience"]: dropdownData.experience.edited.databaseName || "",
      ["industry"]: dropdownData.industry.edited.databaseName || "",
      ["race"]: dropdownData.race.edited.databaseName || "",
      ["gender"]: dropdownData.gender.edited.databaseName || "",
    })
  }
  const handleChangeName = (e) => {
    setHasChanges(true)
    setName({ ...name, editedName: e.target.value })
    setErrors({ ...errors, fullName: nameValidaton(e.target.value) })
  }

  const handleChange = (name, value) => {
    setHasChanges(true)
    const data = objectDeepCopy(dropdownData)
    setDropdownData({
      ...data,
      [name]: {
        ...dropdownData[name],
        edited: value,
      },
    })
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  return (
    <div className={styles.userInfoContainer}>
      <ReportButton
        className={styles.reportIcon}
        anotherUser={anotherUser}
        isBlocked={isBlocked}
        isReported={reported}
        onBlock={() => onBlock(true)}
        onReport={onReport}
        onUnblock={() => onBlock(false)}
        userRole={userRole}
      />
      <Prompt
        when={hasChanges}
        message={messages.CONFIRM_DISCARD_FORM_CHANGES_ON_PAGE_LEAVE}
      />
      {isPopupOpened && hasChanges && (
        <ConfirmCancelPopup
          handleCancel={handleCancel}
          setIsPopupOpened={setIsPopupOpened}
        />
      )}
      <div className={styles.imageContainer}>
        <UserImage
          image={image}
          onUpdate={openPhotoPopup}
          selectedUser={anotherUser}
          onDelete={onDeletePhotoProfile}
        />
      </div>

      {isEditing ? (
        <div className={styles.userInfoInputs}>
          <Input
            fieldStyle="black"
            error={!errors.fullName.valid}
            errorMessage={errors.fullName.errorMessage}
            value={name.editedName}
            onChange={handleChangeName}
            label="Full Name"
            className={styles.editItem}
            autoFocus
          />
          <Dropdown
            fieldStyle="black"
            name="profession"
            options={professionOptions}
            onBlur={() => {}}
            onChange={handleChange}
            value={dropdownData.profession.edited}
            label="Choose your profession"
            className={styles.editItem}
          />
          <Dropdown
            name="experience"
            fieldStyle="black"
            options={experienceOptions}
            onBlur={() => {}}
            onChange={handleChange}
            value={dropdownData.experience.edited}
            label="Choose your experience"
            className={styles.editItem}
          />
          <Dropdown
            name="industry"
            fieldStyle="black"
            options={industryOptions}
            onBlur={() => {}}
            onChange={handleChange}
            value={dropdownData.industry.edited}
            label="Choose your industry"
            className={styles.editItem}
          />
          <Dropdown
            name="gender"
            fieldStyle="black"
            options={genderOptions}
            onBlur={() => {}}
            onChange={handleChange}
            value={dropdownData.gender.edited}
            label="Choose your gender"
            className={styles.editItem}
          />
          <Dropdown
            name="race"
            fieldStyle="black"
            options={raceOptions}
            onBlur={() => {}}
            onChange={handleChange}
            value={dropdownData.race.edited}
            label="Choose your demographics"
            className={styles.editItem}
          />
          <div className={styles.buttonsWrapper}>
            <Button
              className={styles.saveButtons}
              onClick={onCancelClick}
              buttonStyle="outlined"
            >
              Cancel
            </Button>
            <Button
              className={styles.saveButtons}
              onClick={handleSave}
              disabled={checkToSave()}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <UserInfoShowInfo
          name={name}
          anotherUser={anotherUser}
          style={styles}
          dropdownData={dropdownData}
          handleEdit={handleEdit}
          onOpenChat={onOpenChat}
          userType={userType}
        />
      )}
    </div>
  )
}

UserInfo.defaultProps = {
  fullName: "User Name",
}

export default UserInfo
